/**
 * JointJS 그래프 → 용량 플래너 입력 변환.
 *
 * 좌표는 zone 폴리곤과 같은 공간(그래프 절대 좌표)을 써야 하므로
 * element.getBBox().center() 를 쓴다 — cctv-ip-import 의 elementPoint 는
 * 도면 미터 좌표(originalCoords)를 우선하므로 여기엔 맞지 않는다.
 */

import type { dia } from '@joint/core'
import { routeLayerToCategory } from '../../../map-editor-shared/lib/layerUtils'
import {
  findZoneElements,
  getZonePolygon,
  isCctvElement,
  pointInPolygon,
  type XYPoint,
} from '../../editor/lib/zoneCctvRelations'
import { zoneNumberFromName } from '../../nvr-template/lib/nvrTemplate'
import type { CctvKind, PlannerCamera } from '../model/types'

interface CellData {
  layer?: unknown
  type?: unknown
  properties?: Record<string, unknown>
}

function cellData(element: dia.Element): CellData {
  return (element.get('data') as CellData) || {}
}

function cellProps(element: dia.Element): Record<string, unknown> {
  return cellData(element).properties || {}
}

function cellName(element: dia.Element): string {
  const name = cellProps(element).name
  return typeof name === 'string' ? name : ''
}

/** data.layer → category, 없으면 data.type (zoneCctvRelations 와 같은 규칙). */
function elementCategory(element: dia.Element): string {
  const data = cellData(element)
  let category = 'unknown'
  if (data.layer) category = routeLayerToCategory(String(data.layer))
  if (category === 'unknown' && data.type) {
    category = routeLayerToCategory(String(data.type))
  }
  return category
}

/** 전방위(cctv) / 단방향(warningCctv) — 대당 용량이 다르다. */
function cctvKind(element: dia.Element): CctvKind {
  return elementCategory(element) === 'warningCctv' ? 'oneway' : 'omni'
}

interface ExistingZone {
  id: string
  number: number
  polygon: XYPoint[]
}

/**
 * 기존 구역과 그 번호. 번호 추출 규칙은 NVR 템플릿 파일명과 같아야 하므로
 * zoneNumberFromName(이름의 마지막 숫자, 없으면 등장 순서)을 그대로 쓴다.
 */
export function collectExistingZones(graph: dia.Graph): ExistingZone[] {
  return findZoneElements(graph).map((zone, index) => ({
    id: String(zone.id),
    number: Number(zoneNumberFromName(cellName(zone), index + 1)),
    polygon: getZonePolygon(zone),
  }))
}

/**
 * 플래너 입력 카메라. 구역 소속은 cctv.data.properties.zoneId 를 우선하고,
 * 없으면 기존 구역 폴리곤 포함 판정으로 본다 — zoneId 가 없더라도 눈으로 이미
 * 어떤 구역 안에 있는 카메라를 새 구역으로 다시 묶으면 안 된다.
 */
export function collectPlannerCameras(
  graph: dia.Graph,
  existingZones: ExistingZone[]
): PlannerCamera[] {
  const numberById = new Map(existingZones.map(z => [z.id, z.number]))
  const cameras: PlannerCamera[] = []

  for (const element of graph.getElements()) {
    if (!isCctvElement(element)) continue
    const center = element.getBBox().center()
    const props = cellProps(element)
    const zoneId = typeof props.zoneId === 'string' ? props.zoneId : ''

    let zoneNumber = zoneId ? numberById.get(zoneId) ?? null : null
    if (zoneNumber === null) {
      const containing = existingZones.find(z =>
        pointInPolygon({ x: center.x, y: center.y }, z.polygon)
      )
      if (containing) zoneNumber = containing.number
    }

    cameras.push({
      id: String(element.id),
      x: center.x,
      y: center.y,
      kind: cctvKind(element),
      name: typeof props.name === 'string' ? props.name : '',
      zoneNumber,
      ip: typeof props.ipAddress === 'string' ? props.ipAddress.trim() : '',
    })
  }
  return cameras
}

/**
 * CCTV 아이콘 크기의 중앙값. 원 여백(margin)과 순번 부여 시 같은 행으로 볼
 * y 차이(rowTolerance)를 맵 스케일에 맞춰 잡는 데 쓴다 — 맵마다 좌표 단위가
 * 달라 상수로 둘 수 없다.
 */
export function cctvIconMetrics(graph: dia.Graph): { margin: number; rowTolerance: number } {
  const heights = graph
    .getElements()
    .filter(isCctvElement)
    .map(el => el.getBBox().height)
    .filter(h => h > 0)
    .sort((a, b) => a - b)
  if (heights.length === 0) return { margin: 0, rowTolerance: 0 }
  const median = heights[Math.floor(heights.length / 2)]
  return { margin: median / 2, rowTolerance: median }
}
